import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

// Local fallback modules
import { analyzeSemantics } from '@/ai/promptArchitect/semanticAnalyzer';
import { interpretVisuals } from '@/ai/promptArchitect/visualInterpreter';
import { buildProfessionalPrompt } from '@/ai/promptArchitect/promptBuilder';
import type { SemanticElements, VisualInterpretation, BuiltPrompt } from '@/ai/promptArchitect/types';
import { editPromptSequential, generatePromptFromDescription } from '@/lib/openrouterPromptAI';
import { buildSupabaseFunctionUrl, normalizeBaseUrl } from '@/lib/url';

const SUPABASE_URL = normalizeBaseUrl(import.meta.env.VITE_SUPABASE_URL);
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export interface SceneState {
  subject: string;
  action: string;
  environment: string;
  time: string;
  lighting: string;
  weather: string;
  mood: string;
  style: string;
  camera: string;
  composition: string;
  objects: string;
}

export const createEmptySceneState = (): SceneState => ({
  subject: '',
  action: '',
  environment: '',
  time: '',
  lighting: '',
  weather: '',
  mood: '',
  style: '',
  camera: '',
  composition: '',
  objects: '',
});

export interface ArchitectResult {
  sceneState: SceneState;
  prompt: string;
  confidence: number;
  detectedLanguage: string;
  updateType?: string;
  source: 'ai' | 'local';
  // Legacy compat
  semantic?: SemanticElements;
  visual?: VisualInterpretation;
  sections?: Record<string, string>;
  missing?: string[];
}

export function usePromptArchitect() {
  const [result, setResult] = useState<ArchitectResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSceneState, setCurrentSceneState] = useState<SceneState>(createEmptySceneState());
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const debounceRef = useRef<number | null>(null);
  const lastInputRef = useRef<string>('');
  const debounceAbortRef = useRef<AbortController | null>(null);
  const explicitAbortRef = useRef<AbortController | null>(null);

  const runLocalFallback = useCallback((description: string): ArchitectResult => {
    const semantic = analyzeSemantics(description);
    const visual = interpretVisuals(semantic);
    const built = buildProfessionalPrompt(visual);

    const sceneState: SceneState = {
      subject: semantic.elements.subject || '',
      action: semantic.elements.action || '',
      environment: semantic.elements.environment || '',
      time: '',
      lighting: semantic.elements.timeLighting || '',
      weather: '',
      mood: semantic.elements.moodStyle || '',
      style: '',
      camera: '',
      composition: '',
      objects: '',
    };

    return {
      sceneState,
      prompt: built.prompt,
      confidence: semantic.confidence,
      detectedLanguage: semantic.language,
      source: 'local',
      semantic: semantic.elements,
      visual,
    };
  }, []);

  const analyze = useCallback(async (description: string, existingPrompt?: string, _options?: { isExplicit?: boolean }) => {
    const isExplicit = _options?.isExplicit ?? true;
    const clean = description.trim();
    if (!clean) {
      setResult(null);
      setError(null);
      return null;
    }

    const cacheKey = existingPrompt ? `${clean}::${existingPrompt}` : clean;
    if (cacheKey === lastInputRef.current && result) return result;
    lastInputRef.current = cacheKey;

    // Only abort same-priority calls; explicit calls are never aborted by debounced ones
    const ref = isExplicit ? explicitAbortRef : debounceAbortRef;
    if (ref.current) ref.current.abort();

    setIsProcessing(true);
    setError(null);

    const canUseOpenRouter = import.meta.env.VITE_ENABLE_CLIENT_OPENROUTER === 'true' && Boolean(import.meta.env.VITE_OPENROUTER_API_KEY);
    const canUseRemote = Boolean(SUPABASE_URL && SUPABASE_KEY);

    if (canUseOpenRouter) {
      try {
        const previousPrompt = (existingPrompt || currentPrompt || '').trim();
        const aiPrompt = previousPrompt
          ? await editPromptSequential(previousPrompt, clean)
          : await generatePromptFromDescription(clean);

        if (aiPrompt) {
          const semantic = analyzeSemantics(clean);
          const sceneState: SceneState = {
            subject: semantic.elements.subject || '',
            action: semantic.elements.action || '',
            environment: semantic.elements.environment || '',
            time: '',
            lighting: semantic.elements.timeLighting || '',
            weather: '',
            mood: semantic.elements.moodStyle || '',
            style: '',
            camera: '',
            composition: '',
            objects: '',
          };

          const openRouterResult: ArchitectResult = {
            sceneState,
            prompt: aiPrompt,
            confidence: Math.max(semantic.confidence, 85),
            detectedLanguage: semantic.language,
            source: 'ai',
            semantic: semantic.elements,
          };

          setCurrentSceneState(sceneState);
          setCurrentPrompt(aiPrompt);
          setPromptHistory((prev) => [...prev, clean]);
          setResult(openRouterResult);
          setIsProcessing(false);
          return openRouterResult;
        }
      } catch (err) {
        console.warn('[prompt-architect] OpenRouter generation failed, falling back:', err);
      }
    }

    if (!canUseRemote) {
      const local = runLocalFallback(clean);
      setCurrentSceneState(local.sceneState);
      setCurrentPrompt(local.prompt);
      setPromptHistory((prev) => [...prev, clean]);
      setResult(local);
      setIsProcessing(false);
      return local;
    }

    const controller = new AbortController();
    ref.current = controller;

    try {
      const body: Record<string, any> = { description: clean };
      if (existingPrompt?.trim()) body.existingPrompt = existingPrompt.trim();

      // Pass current SceneState for conversational merging
      const hasSceneState = Object.values(currentSceneState).some(v => v.trim());
      if (hasSceneState && existingPrompt?.trim()) {
        body.sceneState = currentSceneState;
      }

      const resp = await fetch(buildSupabaseFunctionUrl(SUPABASE_URL, 'prompt-architect'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (resp.status === 429) {
        toast.error('Rate limit exceeded — please wait and try again.');
        const local = runLocalFallback(clean);
        setCurrentSceneState(local.sceneState);
        setCurrentPrompt(local.prompt);
        setPromptHistory((prev) => [...prev, clean]);
        setResult(local);
        setIsProcessing(false);
        return local;
      }

      if (resp.status === 402) {
        toast.error('AI credits exhausted — add credits in Settings → Workspace → Usage.');
        const local = runLocalFallback(clean);
        setCurrentSceneState(local.sceneState);
        setCurrentPrompt(local.prompt);
        setPromptHistory((prev) => [...prev, clean]);
        setResult(local);
        setIsProcessing(false);
        return local;
      }

      if (!resp.ok) {
        const local = runLocalFallback(clean);
        setCurrentSceneState(local.sceneState);
        setCurrentPrompt(local.prompt);
        setPromptHistory((prev) => [...prev, clean]);
        setResult(local);
        setIsProcessing(false);
        return local;
      }

      const data = await resp.json();

      // Extract SceneState from response
      const newSceneState: SceneState = data.sceneState || {
        subject: data.semantic?.subject || '',
        action: data.semantic?.action || '',
        environment: data.semantic?.environment || '',
        time: '',
        lighting: data.semantic?.timeLighting || '',
        weather: '',
        mood: data.semantic?.moodStyle || '',
        style: '',
        camera: '',
        composition: '',
        objects: '',
      };

      const aiResult: ArchitectResult = {
        sceneState: newSceneState,
        prompt: data.prompt || '',
        confidence: data.confidence ?? 80,
        detectedLanguage: data.detectedLanguage || 'en',
        updateType: data.updateType,
        source: 'ai',
        semantic: data.semantic,
        visual: data.visual,
        sections: data.sections,
        missing: data.missing,
      };

      setCurrentSceneState(newSceneState);
      setCurrentPrompt(aiResult.prompt);
      setPromptHistory((prev) => [...prev, clean]);
      setResult(aiResult);
      setIsProcessing(false);
      return aiResult;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setIsProcessing(false);
        return null;
      }

      console.warn('[prompt-architect] AI failed, using local fallback:', err);
      const local = runLocalFallback(clean);
      setCurrentSceneState(local.sceneState);
      setCurrentPrompt(local.prompt);
      setPromptHistory((prev) => [...prev, clean]);
      setResult(local);
      setIsProcessing(false);
      return local;
    }
  }, [result, runLocalFallback, currentSceneState, currentPrompt]);

  const analyzeDebounced = useCallback((description: string, delay = 800, existingPrompt?: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      analyze(description, existingPrompt, { isExplicit: false });
    }, delay);
  }, [analyze]);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setCurrentSceneState(createEmptySceneState());
    setCurrentPrompt('');
    setPromptHistory([]);
    lastInputRef.current = '';
    if (debounceAbortRef.current) { debounceAbortRef.current.abort(); debounceAbortRef.current = null; }
    if (explicitAbortRef.current) { explicitAbortRef.current.abort(); explicitAbortRef.current = null; }
  }, []);

  return {
    result,
    isProcessing,
    error,
    currentSceneState,
    currentPrompt,
    promptHistory,
    analyze,
    analyzeDebounced,
    reset,
  };
}
